import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SimpleAudioButton from './audio/SimpleAudioButton';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQItem: React.FC<FAQItem> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 py-5">
      <button
        className="flex justify-between items-center w-full text-left"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg font-semibold text-gray-800">{question}</h3>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#C2A4D6]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#C2A4D6]" />
        )}
      </button>
      {isOpen && (
        <div className="mt-3 text-gray-600">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

const FAQ: React.FC = () => {
  const faqs: FAQItem[] = [
    {
      question: "Is FeelHeard a replacement for therapy?",
      answer: "No, FeelHeard is not a replacement for professional therapy or medical advice. It's a supportive companion that can complement therapy, provide emotional support between sessions, or serve as an accessible option when therapy isn't available. For serious mental health concerns, please consult with a qualified professional."
    },
    {
      question: "How is my data kept private and secure?",
      answer: "We take your privacy seriously. All conversations are encrypted end-to-end, and your personal information is never shared with third parties. You have complete control over your data, including the ability to delete your conversation history at any time. We never sell your data to third parties and will never use your data for training."
    },
    {
      question: "Can I cancel my subscription at any time?",
      answer: "Yes, you can cancel your subscription at any time with just two clicks from your account settings. If you cancel, you'll continue to have access until the end of your current billing period. There are no cancellation fees or hidden charges."
    },
    {
      question: "How does the AI know how to respond appropriately?",
      answer: "Our AI is developed with ongoing input from experts in psychology, mental health, and emotional wellness. It recognizes emotional patterns and responds with appropriate empathy, guided by best practices in emotional support and mental health care. The more you interact with it, the better it understands your communication style and emotional needs. However, it's always clear that you're speaking with an AI, not a human."
    },
    {
      question: "What if the AI can't help with my specific issue?",
      answer: "While FeelHeard is designed to provide broad emotional support, there are situations where professional help is more appropriate. In such cases, the AI will acknowledge its limitations and suggest resources for professional support. We maintain a database of crisis resources that can be accessed at any time."
    },
    {
      question: "What platforms is FeelHeard available on?",
      answer: "FeelHeard is currently available as a web application, accessible through any modern web browser. We are actively developing iOS and Android apps, which will be coming soon. Once released, you'll be able to seamlessly continue your conversations across all your devices."
    },
    {
      question: "How does the 7-day free trial work?",
      answer: "Our free trial gives you full access to all Voice Connect features for 7 days. You'll need to provide payment information to start, but we'll send a reminder before your trial ends and you won't be charged if you cancel before the trial period is over. There's no obligation to continue."
    }
  ];

  return (
    <section id="faq" className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block bg-[#F4B6A1]/20 px-4 py-2 rounded-full mb-4">
            <span className="text-[#F4B6A1] font-medium">Common Questions</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3 font-['Poppins']">
            Frequently Asked <span className="text-[#B3D9E6]">Questions</span>
          </h2>
          <div className="flex justify-center mb-6">
            <SimpleAudioButton audioSrc="/assets/audio/faq.mp3" />
          </div>
          <p className="text-lg text-gray-600 font-['Merriweather']">
            Find answers to common questions about FeelHeard and how our AI companion can 
            support your emotional wellbeing.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <FAQItem 
              key={index} 
              question={faq.question} 
              answer={faq.answer} 
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Still have questions? We're here to help.
          </p>
          <a 
            href="#contact" 
            className="inline-block bg-white hover:bg-gray-50 text-[#C2A4D6] border-2 border-[#C2A4D6] py-2 px-6 rounded-full text-lg font-medium transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;